import { createContext, type ReactNode } from 'react';

/**
 * The sidebar entry a page is one tab of, when it is one (spec 224): Integrations,
 * whose tabs are Connections, Triggers and Secrets.
 *
 * A context rather than a prop on every page, as `BandStandingContext` is: each page
 * draws its own `PageHeader`, in its loading and refusal states too, and the section
 * is known once, by the route that groups them. Inside one, the header names the
 * section rather than the page — the selected tab names the page — and the tab strip
 * sits under it. `null` everywhere else, so a page on its own is unchanged.
 *
 * Shared code names no feature: the grouping route decides what goes here.
 */
export interface PageSection {
  /** The section's name, which the header shows as the page's title. */
  title: string;
  /** The strip of the section's tabs, drawn under the header. */
  tabs: ReactNode;
  /**
   * One line for the whole section, in place of each page's own, so the header reads
   * the same whichever tab is open. Absent: each page says its own.
   */
  description?: string;
  /**
   * Whether a page's controls sit in a toolbar row under the tabs rather than in the
   * header (spec 224 review): for a section whose controls change what is shown — the
   * period, the view — they then stand in one place on every tab, and the header
   * never jumps when the tab changes. The page's `actions` lead the row; its `aside`
   * ends it.
   */
  toolbar?: boolean;
  /**
   * The header's surface for every tab of the section (spec 227): `'inverse'` draws
   * the dark band the workspace's main pages carry, with the strip flush under it,
   * so a grouped entry opens like Files does. Absent, `'page'`: each page's own
   * `surface` prop decides, which is how spec 224 left the grouped pages.
   */
  surface?: 'page' | 'inverse';
}

export const PageSectionContext = createContext<PageSection | null>(null);
